import type { TmsInvoiceRow, TmsLoadsReadiness } from '@numeo/types-main-web-app'
import { create } from 'zustand'
import { readCard } from '../api/writeCardApi'

/** The most load numbers one readiness read takes (the card's own input bound). */
const READ_LOAD_NUMBERS_MAX = 50

interface RowsState {
    /** The latest invoicing row of each load, by load number. A write result patches its row here. */
    rows: Record<string, TmsInvoiceRow>
    /** Puts rows in the store, keyed by each load number a row names. Rows already there are replaced. */
    patch: (rows: TmsInvoiceRow[]) => void
    /** Drops the stored rows of loads, so a load that is no longer an invoicing row (not delivered, not found, ambiguous) is not shown as one. */
    forget: (loadNumbers: string[]) => void
    /** Reads the readiness of the named loads (in batches of 50) and patches every row they answer with. */
    refresh: (tabId: string, loadNumbers: string[]) => Promise<TmsLoadsReadiness[]>
}

/** The invoicing rows of one readiness answer: the ready, the needs-documents, the invoiced and the Amazon-billed ones. */
export const invoicingRowsOf = (readiness: TmsLoadsReadiness): TmsInvoiceRow[] => [
    ...readiness.ready,
    ...readiness.needs_documents,
    ...readiness.invoiced.rows,
    ...readiness.billed_through_amazon.rows,
]

/** Splits a list into pieces of at most `size`, keeping the order. */
export const chunked = <T>(items: T[], size: number): T[][] => {
    const pieces: T[][] = []
    for (let index = 0; index < items.length; index += size) pieces.push(items.slice(index, index + size))
    return pieces
}

/** Keyed by load number, so a table row, a drawer and a selection read the same row. Uses the card's readiness read; no other source. */
export const useInvoicingRowsStore = create<RowsState>()((set, get) => ({
    rows: {},
    patch: (rows) =>
        set((state) => {
            const next = { ...state.rows }
            for (const row of rows) for (const loadNumber of row.load_numbers) next[loadNumber] = row
            return { rows: next }
        }),
    forget: (loadNumbers) =>
        set((state) => {
            const next = { ...state.rows }
            for (const loadNumber of loadNumbers) delete next[loadNumber]
            return { rows: next }
        }),
    refresh: async (tabId, loadNumbers) => {
        const answers: TmsLoadsReadiness[] = []
        for (const piece of chunked(loadNumbers, READ_LOAD_NUMBERS_MAX)) {
            const readiness = await readCard<TmsLoadsReadiness>(tabId, 'tms_invoices.worklist', { load_numbers: piece })
            answers.push(readiness)
            const rows = invoicingRowsOf(readiness)
            // A requested load the answer puts in none of its invoicing lists (not delivered, not found, ambiguous, unread) has no row now.
            const answered = new Set(rows.flatMap((row) => row.load_numbers))
            get().patch(rows)
            get().forget(piece.filter((loadNumber) => !answered.has(loadNumber)))
        }
        return answers
    },
}))
